import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getAdminPayments } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { CreditCard, Search, DollarSign, CheckCircle2, XCircle, Clock, AlertTriangle, Filter } from 'lucide-react';

const STATUS_MAP = {
  completed: { label: 'Pagado', color: 'bg-[#2D6A4F]/10 text-[#2D6A4F]', icon: CheckCircle2 },
  pending: { label: 'Pendiente', color: 'bg-[#B8860B]/10 text-[#B8860B]', icon: Clock },
  error: { label: 'Fallido', color: 'bg-[#9B2226]/10 text-[#9B2226]', icon: XCircle },
  rejected: { label: 'Rechazado', color: 'bg-[#9B2226]/10 text-[#9B2226]', icon: XCircle },
  cancelled: { label: 'Cancelado', color: 'bg-[#737373]/10 text-[#737373]', icon: AlertTriangle },
};

const METHOD_MAP = {
  flow: 'Flow.cl',
  external: 'Pago Externo',
  legacy: 'Plataforma',
  platform: 'Plataforma',
};

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await getAdminPayments();
        setPayments(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  useEffect(() => {
    let result = payments;
    if (statusFilter !== 'all') {
      result = result.filter(p => p.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.user_name?.toLowerCase().includes(q) ||
        p.user_email?.toLowerCase().includes(q) ||
        p.course_title?.toLowerCase().includes(q) ||
        p.commerce_order?.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [search, statusFilter, payments]);

  const totalRevenue = payments.filter(p => p.status === 'completed').reduce((s, p) => s + p.amount, 0);
  const countByStatus = (st) => payments.filter(p => p.status === st).length;

  if (loading) return <Layout><div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="admin-payments-page">
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-[#1A1A1A] mb-2">
          Gestion de Pagos
        </h1>
        <p className="text-[#737373] mb-6">{payments.length} pagos registrados</p>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-[#8B1538] text-white border-0">
            <CardContent className="py-4 px-5">
              <DollarSign className="h-5 w-5 mb-1 text-white/70" />
              <p className="text-xs text-white/70">Ingresos Totales</p>
              <p className="text-xl font-bold">${totalRevenue.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3]" data-testid="stat-completed">
            <CardContent className="py-4 px-5">
              <CheckCircle2 className="h-5 w-5 mb-1 text-[#2D6A4F]" />
              <p className="text-xs text-[#737373]">Pagados</p>
              <p className="text-xl font-bold text-[#2D6A4F]">{countByStatus('completed')}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3]" data-testid="stat-failed">
            <CardContent className="py-4 px-5">
              <XCircle className="h-5 w-5 mb-1 text-[#9B2226]" />
              <p className="text-xs text-[#737373]">Fallidos</p>
              <p className="text-xl font-bold text-[#9B2226]">{countByStatus('error') + countByStatus('rejected')}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3]" data-testid="stat-pending">
            <CardContent className="py-4 px-5">
              <Clock className="h-5 w-5 mb-1 text-[#B8860B]" />
              <p className="text-xs text-[#737373]">Pendientes</p>
              <p className="text-xl font-bold text-[#B8860B]">{countByStatus('pending')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#737373]" />
            <Input
              data-testid="search-payments-input"
              placeholder="Buscar por usuario, curso u orden..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-[#E5DFD3]"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-[#737373] self-center" />
            {[
              { key: 'all', label: 'Todos' },
              { key: 'completed', label: 'Pagados' },
              { key: 'error', label: 'Fallidos' },
              { key: 'pending', label: 'Pendientes' },
            ].map(f => (
              <Button
                key={f.key}
                size="sm"
                variant={statusFilter === f.key ? 'default' : 'outline'}
                onClick={() => setStatusFilter(f.key)}
                className={statusFilter === f.key
                  ? 'bg-[#8B1538] hover:bg-[#70102B] text-white'
                  : 'border-[#E5DFD3] text-[#4A4A4A] hover:bg-[#F5F2EA]'}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Payments Table */}
        <Card className="bg-white border border-[#E5DFD3] shadow-sm">
          <CardContent className="pt-4">
            {filtered.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E5DFD3]">
                      <th className="text-left py-3 px-3 text-xs font-medium text-[#737373] uppercase tracking-wide">Usuario</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-[#737373] uppercase tracking-wide">Modulo</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-[#737373] uppercase tracking-wide">Monto</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-[#737373] uppercase tracking-wide">Tipo</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-[#737373] uppercase tracking-wide">Metodo</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-[#737373] uppercase tracking-wide">Estado</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-[#737373] uppercase tracking-wide">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => {
                      const st = STATUS_MAP[p.status] || STATUS_MAP.pending;
                      const Icon = st.icon;
                      return (
                        <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-b border-[#E5DFD3] table-row-hover">
                          <td className="py-3 px-3">
                            <p className="text-sm font-medium text-[#1A1A1A]">{p.user_name}</p>
                            <p className="text-xs text-[#737373]">{p.user_email}</p>
                          </td>
                          <td className="py-3 px-3 text-sm text-[#4A4A4A] max-w-[200px] truncate">{p.course_title}</td>
                          <td className="py-3 px-3 text-sm font-semibold text-[#8B1538]">${p.amount?.toLocaleString()}</td>
                          <td className="py-3 px-3">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#F5F2EA] text-[#4A4A4A]">
                              {p.subscription_type === 'annual' ? 'Anual' : 'Mensual'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-xs text-[#4A4A4A]">
                            {METHOD_MAP[p.payment_method] || p.payment_method || 'Plataforma'}
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
                                <Icon className="h-3 w-3" />
                                {st.label}
                              </span>
                            </div>
                            {p.error_detail && (
                              <p className="text-[10px] text-[#9B2226] mt-1 max-w-[180px] truncate" title={p.error_detail}>
                                {p.error_detail}
                              </p>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            <p className="text-sm text-[#4A4A4A]">{new Date(p.created_at).toLocaleDateString('es-ES')}</p>
                            <p className="text-[10px] text-[#737373]">{new Date(p.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</p>
                            {p.commerce_order && (
                              <p className="text-[10px] font-mono text-[#737373] mt-0.5">{p.commerce_order}</p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="h-12 w-12 mx-auto text-[#D9B48F] mb-3" />
                <p className="text-[#737373]">
                  {payments.length === 0 ? 'No hay pagos registrados' : 'No se encontraron pagos con ese filtro'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
